// P45c — Page /reset-password : consommation du token + nouveau mdp
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Wrench, ArrowLeft, AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useT } from "@/lib/i18n";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";
const resetSchema = z
  .object({
    new_password: z.string().min(8, ht("Mot de passe : 8 caractères minimum")),
    confirm_password: z.string().min(8, "Confirmation requise"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirm_password"],
  });

type ResetInput = z.infer<typeof resetSchema>;

export default function ResetPasswordPage() {
  const t = useT();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Valide le token au mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setTokenError(t("reset_password.no_token"));
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/v1/auth/validate-reset-token?token=${encodeURIComponent(token)}`
        );
        if (!res.ok) throw new Error(`API ${res.status}`);
        const data = await res.json();
        if (data.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setTokenError(t("reset_password.token_invalid"));
        }
      } catch (err) {
        setTokenValid(false);
        setTokenError((err as Error).message);
      }
    })();
  }, [token, t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetInput>({
    resolver: zodResolver(resetSchema),
    defaultValues: { new_password: "", confirm_password: "" },
  });

  const onSubmit = async (data: ResetInput) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/v1/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, new_password: data.new_password }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `API ${res.status}`);
      }
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">{t("common.loading")}</p>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              {t("reset_password.token_invalid_title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{tokenError}</p>
            <Link
              to="/forgot-password"
              className="block text-center text-sm text-primary hover:underline"
            >
              {t("reset_password.request_new_link")}
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <Link
            to="/login"
            className="mb-2 flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("forgot_password.back_to_login")}
          </Link>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            {t("reset_password.title")}
          </CardTitle>
          <CardDescription>{t("reset_password.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4">
              <div className="rounded-md border border-green-500 bg-green-50 p-4 text-sm text-green-900">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle className="h-4 w-4" />
                  {t("reset_password.success_title")}
                </div>
                <p className="mt-2 text-xs">{t("reset_password.success_desc")}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              <div className="space-y-1">
                <label htmlFor="new_password" className="text-sm font-medium">
                  {t("reset_password.new_password")}
                </label>
                <Input
                  id="new_password"
                  type="password"
                  autoComplete="new-password"
                  data-testid="reset-new-password-input"
                  {...register("new_password")}
                />
                {errors.new_password && (
                  <p className="text-xs text-destructive">{errors.new_password.message}</p>
                )}
              </div>

              <div className="space-y-1">
                <label htmlFor="confirm_password" className="text-sm font-medium">
                  {t("reset_password.confirm_password")}
                </label>
                <Input
                  id="confirm_password"
                  type="password"
                  autoComplete="new-password"
                  data-testid="reset-confirm-password-input"
                  {...register("confirm_password")}
                />
                {errors.confirm_password && (
                  <p className="text-xs text-destructive">{errors.confirm_password.message}</p>
                )}
              </div>

              {error && (
                <div className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full"
                data-testid="reset-submit-button"
              >
                {isLoading ? t("reset_password.submitting") : t("reset_password.submit")}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
